(ns ui.dropdown-menu
  (:require ["lucide-react" :refer [CheckIcon]]
            ["@radix-ui/react-dropdown-menu" :as RadixDropdownMenu]
            ["@radix-ui/react-scroll-area" :as RadixScrollArea]
            [applied-science.js-interop :as j]
            [ui.shortcut :refer [shortcut]]
            [ui.utils :refer [clsx separate-props]]))


(defn dropdown-menu-item [& args]
  (let [[props children] (separate-props args)]
    (into [:> RadixDropdownMenu/Item (-> (dissoc props :active)
                                         (update :class #(clsx "relative flex select-none items-center rounded-xs px-2 py-1.5 text-sm outline-hidden transition-colors cursor-pointer focus:bg-kleene-hover-background data-disabled:pointer-events-none data-disabled:opacity-50"
                                                               {"bg-kleene-selected-background" (:active props)}
                                                               %)))]
          (concat children
                  [(when (:shortcut props)
                     [shortcut {:class "ml-auto pl-4 text-red"
                                :keys (:shortcut props)}])]))))


(defn dropdown-menu-checkbox [& args]
  (let [[props children] (separate-props args)
        icon (:icon props)
        parent-props (dissoc props :icon)]
    (into [:> RadixDropdownMenu/CheckboxItem (-> parent-props
                                                 (update :class #(clsx "relative flex cursor-default select-none items-center rounded-xs pr-2 pl-7 py-1.5 text-sm outline-hidden transition-colors focus:bg-kleene-neutral-05 dark:focus:bg-kleene-dark-04 data-disabled:pointer-events-none data-disabled:opacity-50"
                                                                       %)))
           (when icon
             [:> RadixDropdownMenu/ItemIndicator {:class "absolute inline-flex left-1 items-center justify-center w-5 h-5"} [:> icon {:size 14}]])]
          children)))

(defn dropdown-menu-separator [props]
  [:> RadixDropdownMenu/Separator (-> props
                                      (update :class #(clsx "bg-kleene-layout-border h-px w-full" %)))])

(defn dropdown-menu-label [& args]
  (let [[props children] (separate-props args)]
    (into [:> RadixDropdownMenu/Label (-> props
                                          (update :class #(clsx "px-2 py-0.5 text-xs font-medium text-kleene-text-secondary" %)))]
          children)))

(defn dropdown-menu-sub-root [& args]
  (let [[props children] (separate-props args)]
    (into [:> RadixDropdownMenu/Sub props]
          children)))

(defn dropdown-menu-sub-trigger [& args]
  (let [[props children] (separate-props args)]
    (into [:> RadixDropdownMenu/SubTrigger
           (-> props
               (update :class #(clsx "relative flex cursor-default select-none items-center rounded-xs px-2 py-1.5 text-sm outline-hidden transition-colors focus:bg-kleene-neutral-05 dark:focus:bg-kleene-dark-04 data-disabled:pointer-events-none data-disabled:opacity-50"
                                     %)))]
          children)))

(defn dropdown-menu-sub-content [& args]
  (let [[_ children] (separate-props args)]
    [:> RadixDropdownMenu/Portal
     [:> RadixDropdownMenu/SubContent {:class "z-999999 min-w-[8rem] overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-md bg-kleene-background border-kleene-neutral-05 dark:border-kleene-dark-03"}
      [:> RadixScrollArea/Root
       (into [:> RadixScrollArea/Viewport {:class "h-full"
                                           :style {:max-height "min(var(--radix-dropdown-menu-content-available-height), 400px)"
                                                   :overflowY js/undefined}}]
             children)
       [:> RadixScrollArea/Scrollbar {:orientation "vertical" :class "w-1 px-0.5 py-1" :style {:box-sizing "unset"}}
        [:> RadixScrollArea/Thumb {:class "w-full bg-kleene-neutral-02 dark:bg-kleene-dark-03 rounded-md"}]]]]]))

(defn dropdown-menu-sub [& args]
  (let [[{:keys [content]} children] (separate-props args)]
    [dropdown-menu-sub-root {}
     (into [dropdown-menu-sub-trigger] children)
     [dropdown-menu-sub-content content]]))

(defn dropdown-menu-root [& args]
  (let [[props children] (separate-props args)]
    (into [:> RadixDropdownMenu/Root props]
          children)))

(defn dropdown-menu-trigger [& args]
  (let [[props children] (separate-props args)]
    (into [:> RadixDropdownMenu/Trigger (update props
                                                :class #(clsx "outline-hidden focus:outline-hidden" %))]
          children)))

(defn dropdown-menu-content [& args]
  (let [[props children] (separate-props args)]
    [:> RadixDropdownMenu/Portal {:container (:container props)}
     [:> RadixDropdownMenu/Content (-> props
                                       (dissoc :hide-arrow :container)
                                       (update :class #(clsx "z-999999 min-w-[8rem] overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-md bg-kleene-background"
                                                             %)))
      [:> RadixScrollArea/Root
       (into [:> RadixScrollArea/Viewport
              {:class   "h-full"
               :style   {:max-height "min(var(--radix-dropdown-menu-content-available-height), 400px)"
                         :overflowY js/undefined}
               :onWheel (fn [e]
                          (let [target (j/get e :currentTarget)]
                            (when (> (j/get target :scrollHeight) (j/get target :clientHeight))
                              (j/call e :stopPropagation)
                              ;; Manually handle wheel scrolling for overflowing dropdowns because
                              ;; wheel events do not scroll the Radix ScrollArea viewport in this context.
                              (let [delta (condp = (j/get e :deltaMode)
                                            1 (* (j/get e :deltaY) 20)
                                            2 (* (j/get e :deltaY) 400) ;; Page scroll. Matches the 400px max-height above.
                                            (j/get e :deltaY))]
                                (j/assoc! target :scrollTop (+ (j/get target :scrollTop) delta))))))}]
             children)

       [:> RadixScrollArea/Scrollbar {:orientation "vertical" :class "w-1 px-0.5 py-1" :style {:box-sizing "unset"}}
        [:> RadixScrollArea/Thumb {:class "w-full bg-kleene-neutral-02 dark:bg-kleene-dark-03 rounded-md"}]]]
      (when-not (:hide-arrow props)
        [:> RadixDropdownMenu/Arrow {:class "fill-kleene-neutral-05 dark:fill-kleene-dark-03"}])]]))

(defn dropdown-menu-group [& args]
  (let [[props children] (separate-props args)]
    (into [:> RadixDropdownMenu/Group props]
          children)))

(defn dropdown-menu-radio-group [& args]
  (let [[props children] (separate-props args)]
    (into [:> RadixDropdownMenu/RadioGroup props]
          children)))

(defn dropdown-menu-radio-item [& args]
  (let [[props children] (separate-props args)]
    [:> RadixDropdownMenu/RadioItem
     (update props :class #(clsx "relative flex cursor-default items-center gap-1.5 rounded-md py-1 pr-8 pl-1.5 text-sm outline-hidden select-none focus:bg-kleene-hover-background data-inset:pl-7 data-disabled:pointer-events-none data-disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4" %))
     [:span {:class "pointer-events-none absolute right-2 flex items-center justify-center"}
      [:> RadixDropdownMenu/ItemIndicator
       [:> CheckIcon]]]
     (into [:<>] children)]))

(defn dropdown-menu [{:keys [content root-props trigger-as-child props disabled]
                      :or   {trigger-as-child true
                             disabled false}} children]
  [dropdown-menu-root root-props
   [dropdown-menu-trigger {:asChild  trigger-as-child
                           :disabled disabled}
    children]

   [dropdown-menu-content props content]])
